/**
 * Sprint S13.1.B.1 — typical_session_length_min range editor.
 *
 * MVP: 4 number inputs grouped в weekday + weekend pairs. Slider polish
 * deferred к S13.1.B.2 если time. All min/max validated server-side via
 * Pydantic TypicalSessionLength field_validator (≥10, ≤480, max ≥ min).
 */
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface SessionLengthValue {
  weekday_min: number;
  weekday_max: number;
  weekend_min: number;
  weekend_max: number;
}

interface Props {
  label: string;
  hint?: string;
  error?: string;
  value: SessionLengthValue;
  onChange: (val: SessionLengthValue) => void;
  unit?: string;
}

const NUM_INPUT =
  "w-20 rounded-lg border border-cream-border bg-cream-light px-3 py-2 text-[14px] text-navy-deep focus:border-amber/40 focus:outline-none focus:ring-2 focus:ring-amber/60";

export function FieldRangeSlider({
  label,
  hint,
  error,
  value,
  onChange,
  unit,
}: Props) {
  const { t } = useTranslation();
  const unitLabel = unit ?? t("onboarding.fields.minutes_short", { defaultValue: "min" });

  const update = (patch: Partial<SessionLengthValue>) =>
    onChange({ ...value, ...patch });

  return (
    <div>
      <span className="mb-1.5 block text-[13px] font-medium text-navy">
        {label}
      </span>
      {hint ? (
        <span className="mb-3 block text-[12px] text-taupe">{hint}</span>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-cream-border bg-cream-light p-3">
          <p className="mb-2 text-[12px] font-medium text-navy">
            {t("onboarding.fields.session_weekdays", { defaultValue: "Weekdays" })}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={10}
              max={480}
              value={value.weekday_min}
              onChange={(e) =>
                update({ weekday_min: parseInt(e.target.value, 10) || 0 })
              }
              className={NUM_INPUT}
            />
            <span className="text-taupe">—</span>
            <input
              type="number"
              min={10}
              max={480}
              value={value.weekday_max}
              onChange={(e) =>
                update({ weekday_max: parseInt(e.target.value, 10) || 0 })
              }
              className={NUM_INPUT}
            />
            <span className="text-[12px] text-taupe">{unitLabel}</span>
          </div>
        </div>

        <div className="rounded-lg border border-cream-border bg-cream-light p-3">
          <p className="mb-2 text-[12px] font-medium text-navy">
            {t("onboarding.fields.session_weekends", { defaultValue: "Weekends" })}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={10}
              max={480}
              value={value.weekend_min}
              onChange={(e) =>
                update({ weekend_min: parseInt(e.target.value, 10) || 0 })
              }
              className={NUM_INPUT}
            />
            <span className="text-taupe">—</span>
            <input
              type="number"
              min={10}
              max={480}
              value={value.weekend_max}
              onChange={(e) =>
                update({ weekend_max: parseInt(e.target.value, 10) || 0 })
              }
              className={NUM_INPUT}
            />
            <span className="text-[12px] text-taupe">{unitLabel}</span>
          </div>
        </div>
      </div>

      {error ? (
        <span className={cn("mt-2 block text-[12px] text-coral")}>{error}</span>
      ) : null}
    </div>
  );
}
